/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Heading, VStack } from 'chakra-v3';
import { Text } from './Text';
import { EvalsKaryaColors } from '../theme/Colors';

export interface StepCardProps {
  number: number;
  title: string;
  description: string;
}

/**
 * StepCard Component
 *
 * Displays a step with a numbered circle, title, and description.
 * Used in the From Protocol to Insight section.
 *
 * @example
 * ```tsx
 * <StepCard
 *   number={1}
 *   title="Define your study"
 *   description="Upload prompts, model outputs, rubrics"
 * />
 * ```
 */
export const StepCard: React.FC<StepCardProps> = ({ number, title, description }) => {
  return (
    <VStack gap={4} w="100%" alignItems="center">
      <Box
        w="60px"
        h="60px"
        borderRadius="full"
        border="2px solid"
        borderColor="text.teal"
        bg={EvalsKaryaColors.lightBeige}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="24px"
        fontWeight="normal"
        color="text.teal"
        fontFamily="'DM Sans', sans-serif"
        position="relative"
        zIndex={1}
        flexShrink={0}
      >
        {number}
      </Box>
      <VStack gap={{ base: 2, md: '12px' }} textAlign="center" w="100%" maxW={{ base: '100%', md: '330px' }}>
        <Heading
          as="h3"
          fontSize={{ base: '24px', md: '32px' }}
          fontWeight="normal"
          color="text.teal"
          fontFamily="'DM Sans', sans-serif"
          lineHeight="1.4"
          letterSpacing="0.32px"
        >
          {title}
        </Heading>
        <Text size={{ base: 'sm', md: 'md' }} color="text.darkTeal">
          {description}
        </Text>
      </VStack>
    </VStack>
  );
};
